// ACT English Test Bank - Form J08 (October 2025)
// 40 scored questions (50 total on test day, 10 field test questions removed)
// Categories: POW (Production of Writing), KLA (Knowledge of Language), CSE (Conventions of Standard English)

export const formJ08 = {
  id: 'english-J08',
  formCode: 'J08',
  subject: 'english',
  year: 2025,
  month: 'October',
  totalQuestions: 40,
  scoredQuestions: 40,
  timeLimit: 28, // Modified time for scored questions only
  standardTime: 45, // Full test time
  passages: [
    {
      id: 'passage-1',
      number: 1,
      title: 'Seeing Streams in a New Light',
      questionRange: [1, 10],
      description: 'Essay about urban stream daylighting projects'
    },
    {
      id: 'passage-2',
      number: 2,
      title: 'Long Distance',
      questionRange: [11, 20],
      description: 'Personal narrative about Mobile Phone Throwing World Championships in Finland'
    },
    {
      id: 'passage-3',
      number: 3,
      title: 'Threading Connections',
      questionRange: [21, 30],
      description: 'Profile of Cuban artist Alexi Torres and his woven portrait style'
    },
    {
      id: 'passage-4',
      number: 4,
      title: '(Re)Defining Music',
      questionRange: [31, 35],
      description: 'Essay about dip hop, a hip hop subgenre grounded in Deaf culture'
    },
    {
      id: 'passage-5',
      number: 5,
      title: 'Southside Blooms',
      questionRange: [36, 40],
      description: 'Profile of a flower fulfillment center in Chicago\'s Englewood neighborhood'
    }
  ],
  questions: [
    // PASSAGE I: Seeing Streams in a New Light (Q1-10)
    {
      id: 'english-J08-1',
      formId: 'english-J08',
      questionNumber: 1,
      passageId: 'passage-1',
      category: 'CSE',
      isScored: true,
      question: 'As cities grew, in the nineteenth, and twentieth centuries, thousands of urban streams were diverted underground...',
      underlinedText: 'grew, in the nineteenth,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'grew in the nineteenth' },
        { letter: 'C', text: 'grew in the nineteenth,' },
        { letter: 'D', text: 'grew, in the nineteenth' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'No commas are needed between "grew" and the prepositional phrase "in the nineteenth and twentieth centuries." The phrase modifies when the cities grew.'
    },
    {
      id: 'english-J08-2',
      formId: 'english-J08',
      questionNumber: 2,
      passageId: 'passage-1',
      category: 'KLA',
      isScored: true,
      question: 'To alleviate these issues, communities should invest in "daylighting" long-buried waterways...',
      underlinedText: 'To alleviate these issues, communities should invest in',
      questionPrompt: 'Which choice most effectively maintains the essay\'s tone?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'To curtail the predicaments referenced above, communities may want to contemplate investing' },
        { letter: 'H', text: 'But there\'s a conceivable fix if communities would only just invest their monetary assets' },
        { letter: 'J', text: 'One surefire way to remedy the culverting mess is for communities to invest' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The original maintains a clear, professional tone consistent with the rest of the essay. The other options are either overly formal, casual, or wordy.'
    },
    {
      id: 'english-J08-3',
      formId: 'english-J08',
      questionNumber: 3,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: 'Thus, daylighting reduces flooding by allowing more rainwater to be absorbed where it falls.',
      questionPrompt: 'If the writer were to delete the preceding sentence, the essay would primarily lose information that:',
      choices: [
        { letter: 'A', text: 'articulates one advantage of removing barriers between waterways and the soil.' },
        { letter: 'B', text: 'provides data on the effects of removing barriers between waterways and the soil.' },
        { letter: 'C', text: 'describes the process through which soil absorbs rainwater where it falls.' },
        { letter: 'D', text: 'explains the impact of rainwater on daylighting projects.' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The sentence states an advantage (reduced flooding through better rainwater absorption) of daylighting projects, which remove barriers between waterways and soil.'
    },
    {
      id: 'english-J08-4',
      formId: 'english-J08',
      questionNumber: 4,
      passageId: 'passage-1',
      category: 'CSE',
      isScored: true,
      question: 'Furthermore, the rocky bottoms and plant life of daylighted streams, which slow the water\'s flow...',
      underlinedText: 'streams, which slow',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'streams, these' },
        { letter: 'H', text: 'streams that' },
        { letter: 'J', text: 'streams' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'The sentence has a fragment issue. Removing "which" creates a complete sentence where "slow" becomes the main verb for the subject "rocky bottoms and plant life."'
    },
    {
      id: 'english-J08-5',
      formId: 'english-J08',
      questionNumber: 5,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: 'And daylighting saves cities money in other ways, too: open creeks are much cheaper to maintain than culverts are, and daylighting projects have lessened flooding in cities across the United States.',
      underlinedText: 'daylighting projects have lessened flooding in cities across the United States.',
      questionPrompt: 'Given that all the choices are accurate, which one provides the most relevant support for the primary claim in this sentence?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'keeping stormwater out of sewer systems reduces water treatment costs.' },
        { letter: 'C', text: 'natural streambeds actually help clean the water that flows over them.' },
        { letter: 'D', text: 'accessible waterways provide habitats for fish and other wildlife.' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'The sentence is about how daylighting saves cities money. Choice B directly supports this by explaining another way money is saved (reduced water treatment costs).'
    },
    {
      id: 'english-J08-6',
      formId: 'english-J08',
      questionNumber: 6,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: 'Because culverting was historically used to construct buildings over waterways, early attempts at culverting required innovative construction techniques.',
      underlinedText: 'early attempts at culverting required innovative construction techniques.',
      questionPrompt: 'At this point in the essay, the writer wants to introduce a counterclaim that an opponent of daylighting might reasonably assert. Which choice most effectively accomplishes that goal?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'certain daylighting projects tear down abandoned malls and warehouses before adding bridges.' },
        { letter: 'H', text: 'many citizens have lived and worked above their cities\' streams.' },
        { letter: 'J', text: 'a downside of daylighting is the loss of some usable structures.' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'A counterclaim presents an opposing viewpoint. Choice J directly states a downside of daylighting (loss of usable structures), which is what an opponent might argue.'
    },
    {
      id: 'english-J08-7',
      formId: 'english-J08',
      questionNumber: 7,
      passageId: 'passage-1',
      category: 'CSE',
      isScored: true,
      question: '...city officials need to plan daylighting projects in careful collaboration with residents and business owners whom may be effected.',
      underlinedText: 'whom may be effected.',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'whom may be affected.' },
        { letter: 'C', text: 'who may be effected.' },
        { letter: 'D', text: 'who may be affected.' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"Who" is correct because it\'s the subject of "may be affected." "Affected" (not "effected") is the correct verb meaning "influenced or impacted."'
    },
    {
      id: 'english-J08-8',
      formId: 'english-J08',
      questionNumber: 8,
      passageId: 'passage-1',
      category: 'KLA',
      isScored: true,
      question: 'Thoughtfully planned daylighting embellishes the quality of life for the community as a whole...',
      underlinedText: 'embellishes',
      questionPrompt: 'Which choice is clearest and most precise in context?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'refurbishes' },
        { letter: 'H', text: 'enriches' },
        { letter: 'J', text: 'fortifies' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"Enriches" means to improve the quality of something, which fits the context of improving quality of life. "Embellishes" means to decorate or add detail, which doesn\'t fit.'
    },
    {
      id: 'english-J08-9',
      formId: 'english-J08',
      questionNumber: 9,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: 'Nearby homes increase in value, and businesses report higher profits. Citizens far and wide should oppose future culverting initiatives.',
      underlinedText: 'Citizens far and wide should oppose future culverting initiatives.',
      questionPrompt: 'Which choice provides the most effective conclusion for the paragraph and essay?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'In the last twenty years, cities worldwide have contemplated daylighting various streams.' },
        { letter: 'C', text: 'Daylighting offers more than a view of a waterway; it brings life back to the city center.' },
        { letter: 'D', text: 'Daylighting is doable when people work together, but it isn\'t easy.' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'Choice C provides an effective conclusion by summarizing the broader benefits of daylighting in a positive, forward-looking way that ties together the essay\'s themes.'
    },
    {
      id: 'english-J08-10',
      formId: 'english-J08',
      questionNumber: 10,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: 'The writer is considering adding the following assertion to the essay: "(If it had happened sooner, the project might have saved $70 million more in flood damage that occurred between 1999 and 2011.)"',
      questionPrompt: 'If the writer were to add the sentence, it would most logically be placed at:',
      choices: [
        { letter: 'F', text: 'Point A in Paragraph 1.' },
        { letter: 'G', text: 'Point B in Paragraph 3.' },
        { letter: 'H', text: 'Point C in Paragraph 3.' },
        { letter: 'J', text: 'Point D in Paragraph 4.' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: 'Point C follows the discussion of the Bee Branch Creek project in Dubuque, which is referenced by "the project" in the proposed sentence. The timeline (1999-2011 flood damage) connects to when the project was completed.'
    },

    // PASSAGE II: Long Distance (Q11-20)
    {
      id: 'english-J08-11',
      formId: 'english-J08',
      questionNumber: 11,
      passageId: 'passage-2',
      category: 'KLA',
      isScored: true,
      question: 'I\'ve definitely felt the urge, during a few annoyingly irritating calls with my sister, to want to hurl my phone across the room.',
      underlinedText: 'a few annoyingly irritating calls with my sister, to want to hurl my phone',
      questionPrompt: 'Which choice is least redundant in context?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'aggravating calls with my sister that make me long to throw my phone,' },
        { letter: 'C', text: 'some frustrating calls with my sister on the phone,' },
        { letter: 'D', text: 'certain exasperating calls with my sister,' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"Annoyingly irritating" is redundant (both words mean the same thing). Choice D uses one word ("exasperating") and removes "to want" since "felt the urge... to hurl" already implies wanting.'
    },
    {
      id: 'english-J08-12',
      formId: 'english-J08',
      questionNumber: 12,
      passageId: 'passage-2',
      category: 'KLA',
      isScored: true,
      question: '...this competition wasn\'t inspired by annoying siblings; it was ascertained as an engaging reminder for Finns to dispose of their mobile phones responsibly.',
      underlinedText: 'ascertained',
      questionPrompt: 'Which choice is clearest and most precise in context?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'speculated' },
        { letter: 'H', text: 'conceived' },
        { letter: 'J', text: 'supposed' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"Conceived" means created or thought up, which fits the context of the competition being designed/created as a reminder. "Ascertained" means determined or found out, which doesn\'t fit.'
    },
    {
      id: 'english-J08-13',
      formId: 'english-J08',
      questionNumber: 13,
      passageId: 'passage-2',
      category: 'KLA',
      isScored: true,
      question: 'Beforehand, though, contestants dig through the tubs of outdated models, searching for the phone with the greatest flight potential.',
      underlinedText: 'searching for the phone with the greatest flight potential.',
      questionPrompt: 'Which choice most effectively maintains the essay\'s tone?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'in pursuit of the phone with which an elongated lob could most likely be achieved.' },
        { letter: 'C', text: 'seeking the phone with a structural configuration most suited to being flung afar.' },
        { letter: 'D', text: 'keeping their eyes peeled for the phone that might take to the skies with gusto.' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The original maintains the casual, slightly humorous tone of the essay. The other choices are either too formal (B, C) or too informal/quirky (D).'
    },
    {
      id: 'english-J08-14',
      formId: 'english-J08',
      questionNumber: 14,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'I found the lively, agitated atmosphere (with no trace of the fierce rivalry I often saw at competitive events) to be a pleasant surprise.',
      underlinedText: 'lively, agitated',
      questionPrompt: 'Which choice creates the clearest contrast between the atmosphere at this event and the fierce rivalry the narrator expected to see?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'dignified, focused' },
        { letter: 'H', text: 'festive, cordial' },
        { letter: 'J', text: 'tense, wary' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"Festive, cordial" contrasts with "fierce rivalry." "Agitated" (F) and "tense, wary" (J) would suggest rivalry, not contrast with it. "Dignified, focused" (G) doesn\'t match the fun atmosphere described.'
    },
    {
      id: 'english-J08-15',
      formId: 'english-J08',
      questionNumber: 15,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'Some attendees showed off themed or zany costumes, but most people wore casual, everyday clothes. However, the only telltale signs of who was competing were the paper bib numbers...',
      underlinedText: 'However,',
      questionPrompt: 'Which transition word or phrase is most logical in context?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'Eventually,' },
        { letter: 'C', text: 'Besides,' },
        { letter: 'D', text: 'In fact,' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"In fact" works to emphasize the point that distinguishing competitors was difficult. "However" suggests contrast that doesn\'t exist between the sentences.'
    },
    {
      id: 'english-J08-16',
      formId: 'english-J08',
      questionNumber: 16,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'The crowd cheered as a woman rode a unicycle in a circle for a full forty-five seconds before lofting her phone to the sky.',
      underlinedText: 'for a full forty-five seconds',
      questionPrompt: 'If the writer were to delete the underlined portion, the essay would primarily lose a specific detail that:',
      choices: [
        { letter: 'F', text: 'helps clarify that the woman on the unicycle was the most impressive contestant.' },
        { letter: 'G', text: 'helps illustrate why the woman\'s performance was particularly noteworthy.' },
        { letter: 'H', text: 'defines the minimum required length of the performances in this event.' },
        { letter: 'J', text: 'reveals why the woman chose to ride a unicycle before throwing her phone.' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: 'The time detail (forty-five seconds) shows the sustained difficulty of riding a unicycle in a circle before throwing, making her performance noteworthy in the Freestyle event.'
    },
    {
      id: 'english-J08-17',
      formId: 'english-J08',
      questionNumber: 17,
      passageId: 'passage-2',
      category: 'CSE',
      isScored: true,
      question: 'We stopped to watch the Freestyle event, which is judged more on the creativity of the throws than on their distance.',
      underlinedText: 'more on',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'at least as much' },
        { letter: 'C', text: 'primarily' },
        { letter: 'D', text: 'Delete the underlined portion.' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: '"More on... than on" is the correct comparative structure. The sentence compares creativity vs. distance in judging, so "more... than" is appropriate.'
    },
    {
      id: 'english-J08-18',
      formId: 'english-J08',
      questionNumber: 18,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: '[1] The crowd cheered as a woman rode a unicycle... [2] The contestant after that drew whistles and whoops when... [3] We stopped to watch the Freestyle event...',
      questionPrompt: 'Which sequence of sentences makes this paragraph most logical?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: '1, 3, 2' },
        { letter: 'H', text: '3, 1, 2' },
        { letter: 'J', text: '3, 2, 1' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: 'Sentence 3 introduces what they\'re watching (the Freestyle event), so it should come first. Then sentences 1 and 2 give examples of performances they saw, in order.'
    },
    {
      id: 'english-J08-19',
      formId: 'english-J08',
      questionNumber: 19,
      passageId: 'passage-2',
      category: 'CSE',
      isScored: true,
      question: 'Spectators didn\'t talk on their phones; they talked to each other.',
      underlinedText: 'phones; they talked',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'phones, instead, they talked' },
        { letter: 'C', text: 'phones, they were talking' },
        { letter: 'D', text: 'phones instead talking' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'A semicolon correctly joins two independent clauses. Choice B creates a comma splice, C is also a comma splice, and D is grammatically incomplete.'
    },
    {
      id: 'english-J08-20',
      formId: 'english-J08',
      questionNumber: 20,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'Following their lead, I turned my own phone off.',
      questionPrompt: 'Which of the following sentences, if added here, would most effectively suggest that at this moment the narrator intends to connect with others in person rather than engaging with the phone?',
      choices: [
        { letter: 'F', text: 'After all, I had taken enough pictures and video to remember this experience in the years to come.' },
        { letter: 'G', text: 'Then I linked arms with Anneli and stepped further into the crowd.' },
        { letter: 'H', text: 'At that moment, I started hoping that I could come again next year.' },
        { letter: 'J', text: 'I could still feel it in my pocket, but I ignored it—mostly.' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: 'Choice G shows the narrator physically connecting with her cousin (linking arms) and engaging with the crowd, demonstrating in-person connection over phone use.'
    },

    // PASSAGE III: Threading Connections (Q21-30)
    {
      id: 'english-J08-21',
      formId: 'english-J08',
      questionNumber: 21,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'Interconnectedness eventually became the central theme of Torres\'s art in 2016, with his Sun Light series, he paid homage to the people and place that first inspired him.',
      underlinedText: 'art in 2016, with',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'art, and' },
        { letter: 'C', text: 'art and' },
        { letter: 'D', text: 'art,' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'The sentence as written is a comma splice. Adding "and" after the comma creates a compound sentence correctly joining the two independent clauses. The year reference moves elsewhere.'
    },
    {
      id: 'english-J08-22',
      formId: 'english-J08',
      questionNumber: 22,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'Torres created sixteen oil portraits of friends and family from Bermeja, starting and completing each portrait in the same lunar phase in which the village\'s residents farm.',
      underlinedText: 'starting and completing each portrait in the same lunar phase in which the village\'s residents farm.',
      questionPrompt: 'If the writer were to delete the underlined portion (adjusting the punctuation as needed), the paragraph would primarily lose:',
      choices: [
        { letter: 'F', text: 'a detail illustrating how Torres\'s approach to creating the Sun Light series was influenced by his personal and cultural history.' },
        { letter: 'G', text: 'an example clarifying how Torres depicted various types of moonlight in the paintings in his Sun Light series.' },
        { letter: 'H', text: 'a suggestion that Torres often finds inspiration for his art from a connection with friends and family.' },
        { letter: 'J', text: 'a description of Torres\'s upbringing and the cultural traditions that most influenced his artistry.' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The deleted portion explains that Torres painted during the same lunar phases his village uses for farming—connecting his artistic process to his personal and cultural history.'
    },
    {
      id: 'english-J08-23',
      formId: 'english-J08',
      questionNumber: 23,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'To visually represent interconnectedness, Torres painted the portraits—in his signature, "woven" style.',
      underlinedText: 'portraits—in his signature,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'portraits, in his signature,' },
        { letter: 'C', text: 'portraits in his signature' },
        { letter: 'D', text: 'portraits, in his signature' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'No punctuation is needed. "In his signature \'woven\' style" is a prepositional phrase modifying how he painted, and should flow directly from "portraits."'
    },
    {
      id: 'english-J08-24',
      formId: 'english-J08',
      questionNumber: 24,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'The portraits appear as though they\'re made from strands of hemp or jute that have been assembled to form lifelike faces.',
      underlinedText: 'assembled to',
      questionPrompt: 'Which choice provides the most specific description of the appearance of the "strands" composing the faces in Torres\'s portraits?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'combined in such a way that they' },
        { letter: 'H', text: 'precisely interlaced to' },
        { letter: 'J', text: 'artistically used to' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"Precisely interlaced" specifically describes the woven appearance of the strands, matching the "woven style" mentioned earlier. "Assembled" and "combined" are vague.'
    },
    {
      id: 'english-J08-25',
      formId: 'english-J08',
      questionNumber: 25,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'To achieve this effect, Torres worked from photographs to draw realistic renderings of his subjects directly onto giant canvases...',
      underlinedText: 'photographs to draw',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'photographs, which would draw' },
        { letter: 'C', text: 'photographs, who drew' },
        { letter: 'D', text: 'photographs that drew' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The original is correct. "Worked from photographs to draw" shows Torres\'s action. The other choices incorrectly suggest the photographs are doing the drawing.'
    },
    {
      id: 'english-J08-26',
      formId: 'english-J08',
      questionNumber: 26,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: '...of his subjects directly onto giant canvases in his studio in Atlanta, Georgia. For instance, he applied thousands of thin, fiber-like dabs of paint...',
      underlinedText: 'For instance,',
      questionPrompt: 'Which transition word or phrase is most logical in context?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'Consequently,' },
        { letter: 'H', text: 'Again,' },
        { letter: 'J', text: 'Then,' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: '"Then" indicates the next step in Torres\'s process. The sentence describes what he did after drawing the renderings. "For instance" would introduce an example, not a sequential step.'
    },
    {
      id: 'english-J08-27',
      formId: 'english-J08',
      questionNumber: 27,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: '...he applied thousands of thin, fiber-like dabs of paint, mostly—whites, grays, and blacks—to give the multilayered portraits the appearance of being rough and woven.',
      underlinedText: 'paint, mostly—whites, grays, and blacks—to',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'paint—mostly whites, grays, and blacks—' },
        { letter: 'C', text: 'paint, (mostly whites, grays, and blacks),' },
        { letter: 'D', text: 'paint—mostly whites, grays, and blacks,' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'Em dashes should be used in pairs to set off parenthetical information. Choice B correctly uses matching em dashes around "mostly whites, grays, and blacks."'
    },
    {
      id: 'english-J08-28',
      formId: 'english-J08',
      questionNumber: 28,
      passageId: 'passage-3',
      category: 'KLA',
      isScored: true,
      question: 'In Sun Light—Mario, an older man\'s skin possesses a checkered texture, so the salt-and-pepper hairs of his handlebar mustache appear sharp and wiry.',
      underlinedText: 'so',
      questionPrompt: 'Which choice is clearest and most precise in context?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'since' },
        { letter: 'H', text: 'and' },
        { letter: 'J', text: 'for' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"And" correctly joins two parallel observations about the portrait. "So" implies causation (the texture causes the mustache appearance), which doesn\'t make sense.'
    },
    {
      id: 'english-J08-29',
      formId: 'english-J08',
      questionNumber: 29,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'He likens his artistic process to how his family has, for generations, cultivated and harvested crops: "With my works, it is as if I am planting an idea and working with universal laws in their realization."',
      underlinedText: '"With my works, it is as if I am planting an idea and working with universal laws in their realization."',
      questionPrompt: 'Given that all the choices are actual quotations from Torres, which one would most effectively conclude the sentence and the essay?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: '"Even if it looks like a lot of effort to create my paintings, they are effortless because it is total joy to make them."' },
        { letter: 'C', text: '"Most of the Cuban population is made up of farmers who plant and harvest their crops on the waning moon."' },
        { letter: 'D', text: '"I started to paint objects and people made out of natural elements, like feathers and basket weaving."' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The original quote directly connects Torres\'s art to farming ("planting an idea"), reinforcing the interconnectedness theme and the comparison to his family\'s agricultural traditions.'
    },
    {
      id: 'english-J08-30',
      formId: 'english-J08',
      questionNumber: 30,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'Question about the passage as a whole.',
      questionPrompt: 'Suppose the writer\'s primary purpose had been to discuss paintings that represent a key theme in an artist\'s work. Would this essay accomplish that purpose?',
      choices: [
        { letter: 'F', text: 'Yes, because it explains Torres\'s philosophy that childhood relationships have the most profound influence on an artist\'s paintings.' },
        { letter: 'G', text: 'Yes, because it explores how Torres\'s Sun Light series exemplifies his philosophy about interconnectedness.' },
        { letter: 'H', text: 'No, because although it discusses major ideas that Torres has explored throughout his artistic career, it doesn\'t focus on particular artworks.' },
        { letter: 'J', text: 'No, because although it describes Torres\'s Sun Light portraits in detail, it fails to discuss thematic elements in the series.' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: 'The essay does accomplish this purpose—it discusses specific paintings (Sun Light series) and explains how they represent Torres\'s key theme of interconnectedness.'
    },

    // PASSAGE IV: (Re)Defining Music (Q31-35)
    {
      id: 'english-J08-31',
      formId: 'english-J08',
      questionNumber: 31,
      passageId: 'passage-4',
      category: 'CSE',
      isScored: true,
      question: 'Dip hop—a growing subgenre of hip hop grounded in Deaf culture and aesthetics—challenge stereotypes and help to reshape mainstream notions of what music is.',
      underlinedText: 'challenge stereotypes and help',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'are challenging stereotypes and have helped' },
        { letter: 'C', text: 'challenge stereotypes and are helping' },
        { letter: 'D', text: 'challenges stereotypes and is helping' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"Dip hop" is a singular subject, so it requires singular verbs: "challenges" and "is helping." The original uses plural verbs that don\'t agree with the singular subject.'
    },
    {
      id: 'english-J08-32',
      formId: 'english-J08',
      questionNumber: 32,
      passageId: 'passage-4',
      category: 'CSE',
      isScored: true,
      question: 'Though rooted in hip hop, dip hop is not simply rap songs translated into ASL but musical expression from a Deaf perspective...',
      underlinedText: 'hip hop, dip hop is not simply rap songs translated into ASL but musical expression',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'hip hop, dip hop is not simply, rap songs translated into ASL, but musical expression' },
        { letter: 'H', text: 'hip hop—dip hop is not simply rap songs, translated into ASL but musical expression,' },
        { letter: 'J', text: 'hip hop, dip hop is not simply rap songs translated into ASL but—musical expression' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The original correctly uses the "not... but" parallel structure without unnecessary punctuation. The other choices add incorrect or confusing punctuation.'
    },
    {
      id: 'english-J08-33',
      formId: 'english-J08',
      questionNumber: 33,
      passageId: 'passage-4',
      category: 'KLA',
      isScored: true,
      question: 'Some collaborate with DJs, deaf or hearing, some induct visual elements like sound-activated lights...',
      underlinedText: 'induct',
      questionPrompt: 'Which choice is clearest and most precise in context?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'congregate' },
        { letter: 'C', text: 'compound' },
        { letter: 'D', text: 'integrate' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"Integrate" means to combine or incorporate, which fits the context of adding visual elements to performances. "Induct" means to formally admit someone to a group.'
    },
    {
      id: 'english-J08-34',
      formId: 'english-J08',
      questionNumber: 34,
      passageId: 'passage-4',
      category: 'CSE',
      isScored: true,
      question: 'There are artists who only sign their raps, and those who incorporate both oral and sign languages to make their music accessible to hearing audiences.',
      underlinedText: 'who only sign their raps, and those who',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'whom only sign their raps, and those whom' },
        { letter: 'H', text: 'which only sign their raps, and them that' },
        { letter: 'J', text: 'who only sign their raps, and them which' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: '"Who" is correct for referring to people as subjects of clauses. "Whom" would be used for objects, and "which" is for things, not people.'
    },
    {
      id: 'english-J08-35',
      formId: 'english-J08',
      questionNumber: 35,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'But many have yet to encounter dip hop. In 2020, dip hop artist Sean Forbes\'s "Little Victories" reached number one in hip hop on iTunes...',
      underlinedText: 'But many have yet to encounter dip hop.',
      questionPrompt: 'Which choice most effectively leads the reader from the preceding paragraph to the information that follows in this paragraph?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'And this is just one of many styles of rap that have developed over time.' },
        { letter: 'C', text: 'And dip hop is resonating.' },
        { letter: 'D', text: 'But each artist is unique.' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: '"And dip hop is resonating" provides a transition to the following examples of dip hop\'s success (charting hits, Super Bowl performance). It sets up the evidence of growing popularity.'
    },

    // PASSAGE V: Southside Blooms (Q36-40)
    {
      id: 'english-J08-36',
      formId: 'english-J08',
      questionNumber: 36,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'In Chicago\'s Englewood neighborhood, every employee at Southside Blooms, a flower fulfillment center, have embraced the motto painted on the wall...',
      underlinedText: 'have embraced',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'are embracing' },
        { letter: 'H', text: 'embraces' },
        { letter: 'J', text: 'embrace' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"Every employee" is a singular subject and requires a singular verb: "embraces" (present) or "has embraced" (present perfect). "Have embraced" is plural.'
    },
    {
      id: 'english-J08-37',
      formId: 'english-J08',
      questionNumber: 37,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'The employees—all neighborhood residents and most of whom are under the age of thirty arrange peonies, hyacinths, and tulips...',
      underlinedText: 'thirty',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'thirty—' },
        { letter: 'C', text: 'thirty,' },
        { letter: 'D', text: 'thirty:' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'Em dashes must be used in pairs. The sentence opens with an em dash after "employees," so a closing em dash is needed after "thirty" to complete the parenthetical phrase.'
    },
    {
      id: 'english-J08-38',
      formId: 'english-J08',
      questionNumber: 38,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'For two years, "farm team" member Dionta White has planted and cultivated flowers on five vacant city lots...',
      underlinedText: 'member Dionta White',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'member, Dionta White,' },
        { letter: 'H', text: 'member, Dionta White' },
        { letter: 'J', text: 'member Dionta White,' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'No commas are needed. "Dionta White" is essential to identifying which "farm team member" is being discussed, so it shouldn\'t be set off with commas.'
    },
    {
      id: 'english-J08-39',
      formId: 'english-J08',
      questionNumber: 39,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'For White, it\'s been revelatory, he\'s learning about eco-friendly methods that he didn\'t know existed.',
      underlinedText: 'revelatory, he\'s learning',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'NO CHANGE' },
        { letter: 'B', text: 'revelatory, he has learned' },
        { letter: 'C', text: 'revelatory; he\'s learning' },
        { letter: 'D', text: 'revelatory; to learn' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'The original is a comma splice (two independent clauses joined only by a comma). A semicolon correctly joins the two independent clauses.'
    },
    {
      id: 'english-J08-40',
      formId: 'english-J08',
      questionNumber: 40,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'In four years, over thirty Southside Blooms employees have earned stipends that study urban agriculture.',
      underlinedText: 'employees have earned stipends that',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'NO CHANGE' },
        { letter: 'G', text: 'employees who have earned stipends to' },
        { letter: 'H', text: 'employees, having earned stipends, to' },
        { letter: 'J', text: 'employees have earned stipends to' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'The stipends are earned in order "to study" (infinitive of purpose), not stipends "that study" (which incorrectly suggests the stipends do the studying).'
    }
  ]
};

// Scoring table for Form J08 English (40 scored questions → 1-36 scale)
export const scoringTableJ08 = {
  40: 36, 39: 35, 38: 35, 37: 35, 36: 34, 35: 33, 34: 32,
  33: 30, 32: 29, 31: 28, 30: 27, 29: 26, 28: 25, 27: 25,
  26: 24, 25: 23, 24: 23, 23: 22, 22: 21, 21: 21, 20: 20,
  19: 20, 18: 19, 17: 18, 16: 16, 15: 15, 14: 15, 13: 14,
  12: 13, 11: 12, 10: 11, 9: 11, 8: 10, 7: 10, 6: 9,
  5: 8, 4: 7, 3: 5, 2: 4, 1: 2, 0: 1
};

export default formJ08;
